import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  Banknote,
  CalendarRange,
  FileText,
  Landmark,
  TrendingUp,
  UsersRound,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { SkeletonText } from '@/components/ui/Skeleton'
import { StatCard } from '@/components/ui/StatCard'
import { yearsQuery } from '@/features/academic/api'
import {
  collectionsQuery,
  outstandingByClassQuery,
  overviewQuery,
  topDebtorsQuery,
} from '@/features/finance/api'
import { MoneyBarChart, RankedMoneyBars } from '@/features/finance/components/Charts'
import { methodLabel } from '@/features/finance/status'
import { useMoney } from '@/features/finance/useCurrency'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { intlLocale } from '@/i18n'
import { formatDate, formatMonthShort, formatNumber } from '@/utils/format'

function iso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** "2026-09-25" → "Sep 25" / "25 sept." in the active language. */
function dayMonth(date: string): string {
  const d = new Date(`${date}T00:00:00`)
  return Number.isNaN(d.getTime())
    ? date
    : new Intl.DateTimeFormat(intlLocale(), { day: 'numeric', month: 'short' }).format(d)
}

export function FinanceReportsPage() {
  const { t } = useTranslation('finance')
  useDocumentTitle('Finance reports')
  const { money, currency } = useMoney()
  const now = new Date()
  const [range, setRange] = useState({
    from: iso(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: iso(now),
  })
  const years = useQuery(yearsQuery())
  const [yearId, setYearId] = useState('')
  const overview = useQuery(overviewQuery())
  const collections = useQuery({
    ...collectionsQuery(range.from, range.to),
    enabled: Boolean(range.from && range.to && range.from <= range.to),
  })
  const byClass = useQuery(outstandingByClassQuery(yearId || undefined))
  const debtors = useQuery(topDebtorsQuery(10))
  const o = overview.data

  return (
    <>
      <PageHeader
        title={t('Finance reports')}
        description={t('Live figures from invoices and payments — nothing here is cached.')}
        breadcrumbs={[{ label: t('Finance') }, { label: t('Reports') }]}
        homeHref="/admin"
      />

      {overview.error ? (
        <Card className="mb-6">
          <ErrorState compact onRetry={() => void overview.refetch()} />
        </Card>
      ) : (
        <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard
            label={t('Collected this month')}
            value={o ? money(o.collectedThisMonth) : null}
            icon={<Banknote />}
            tone="success"
            loading={overview.isPending}
          />
          <StatCard
            label={t('Outstanding')}
            value={o ? money(o.outstandingTotal) : null}
            icon={<Landmark />}
            tone="brand"
            loading={overview.isPending}
          />
          <StatCard
            label={t('Overdue invoices')}
            value={o ? formatNumber(o.overdueCount) : null}
            icon={<AlertTriangle />}
            tone="danger"
            loading={overview.isPending}
            hint={o ? t('{{amount}} past due', { amount: money(o.overdueTotal) }) : undefined}
          />
          <StatCard
            label={t('Invoices this month')}
            value={o ? formatNumber(o.invoicesThisMonth) : null}
            icon={<FileText />}
            tone="info"
            loading={overview.isPending}
          />
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-2">
        <Card>
          <CardHeader
            title={t('Collections by month')}
            description={t('Valid payments over the last 12 months')}
            icon={<TrendingUp />}
          />
          <CardBody>
            {o ? (
              <MoneyBarChart
                data={o.collectionsByMonth.map((m) => ({
                  key: m.month,
                  label: formatMonthShort(m.month),
                  value: m.value,
                }))}
                currency={currency}
                ariaLabel={t('Collections per month over the last 12 months')}
                keyHeader={t('Month')}
              />
            ) : (
              <SkeletonText lines={6} />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('Collections by day')}
            description={
              collections.data
                ? t('{{count}} payments · {{amount}}', {
                    count: collections.data.count,
                    formatted: formatNumber(collections.data.count),
                    amount: money(collections.data.total),
                  })
                : t('Choose a date range')
            }
            icon={<CalendarRange />}
          />
          <div className="flex flex-col gap-2 border-b border-line px-5 py-3 sm:flex-row">
            <label htmlFor="collections-from" className="flex items-center gap-2 text-xs text-muted">
              {t('From')}
              <Input
                id="collections-from"
                type="date"
                size="sm"
                value={range.from}
                max={range.to}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </label>
            <label htmlFor="collections-to" className="flex items-center gap-2 text-xs text-muted">
              {t('To')}
              <Input
                id="collections-to"
                type="date"
                size="sm"
                value={range.to}
                min={range.from}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </label>
          </div>
          <CardBody className="space-y-6">
            {collections.error ? (
              <ErrorState compact onRetry={() => void collections.refetch()} />
            ) : !collections.data ? (
              <SkeletonText lines={6} />
            ) : collections.data.count === 0 ? (
              <EmptyState title={t('No payments in this range')} compact />
            ) : (
              <>
                <MoneyBarChart
                  data={collections.data.byDay.map((d) => ({
                    key: d.date,
                    label: dayMonth(d.date),
                    value: Number(d.total),
                  }))}
                  currency={currency}
                  ariaLabel={t('Collections per day in the selected range')}
                  keyHeader={t('Day')}
                />
                <section aria-labelledby="by-method">
                  <h3 id="by-method" className="mb-3 text-sm font-semibold">
                    {t('By method')}
                  </h3>
                  <RankedMoneyBars
                    ariaLabel={t('Collections by payment method')}
                    currency={currency}
                    rows={collections.data.byMethod.map((m) => ({
                      key: m.method,
                      label: t(methodLabel[m.method]),
                      value: Number(m.total),
                      hint: t('{{count}} payments', { count: m.count, formatted: formatNumber(m.count) }),
                    }))}
                  />
                </section>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('Outstanding dues by class')}
            description={t('Open balances on issued invoices')}
            icon={<Landmark />}
            actions={
              <Select
                aria-label={t('Academic year')}
                size="sm"
                className="w-36"
                placeholder={t('All years')}
                value={yearId}
                onChange={(e) => setYearId(e.target.value)}
                options={(years.data ?? []).map((y) => ({ value: y.id, label: y.name }))}
              />
            }
          />
          <CardBody>
            {byClass.error ? (
              <ErrorState compact onRetry={() => void byClass.refetch()} />
            ) : !byClass.data ? (
              <SkeletonText lines={6} />
            ) : byClass.data.length === 0 ? (
              <EmptyState
                title={t('Nothing outstanding')}
                description={t('Every issued invoice is settled.')}
                compact
              />
            ) : (
              <>
                <RankedMoneyBars
                  ariaLabel={t('Outstanding dues by class')}
                  currency={currency}
                  rows={byClass.data.map((c) => ({
                    key: c.classId,
                    label: c.className,
                    value: Number(c.totalDue),
                    hint: t('{{count}} students', {
                      count: c.studentCount,
                      formatted: formatNumber(c.studentCount),
                    }),
                  }))}
                />
                <details className="mt-4 text-xs text-muted">
                  <summary className="cursor-pointer select-none hover:text-ink">
                    {t('View as table')}
                  </summary>
                  <table className="mt-2 w-full text-left">
                    <thead>
                      <tr>
                        <th className="py-1 font-medium">{t('Class')}</th>
                        <th className="py-1 text-right font-medium">{t('Invoices')}</th>
                        <th className="py-1 text-right font-medium">{t('Due')}</th>
                        <th className="py-1 text-right font-medium">{t('Overdue')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byClass.data.map((c) => (
                        <tr key={c.classId} className="border-t border-line">
                          <td className="py-1">{c.className}</td>
                          <td className="py-1 text-right tabular">{c.invoiceCount}</td>
                          <td className="py-1 text-right tabular">{money(c.totalDue)}</td>
                          <td className="py-1 text-right tabular">{money(c.overdueAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title={t('Top debtors')}
            description={t('Largest outstanding balances')}
            icon={<UsersRound />}
          />
          {debtors.error ? (
            <ErrorState compact onRetry={() => void debtors.refetch()} />
          ) : !debtors.data ? (
            <CardBody>
              <SkeletonText lines={6} />
            </CardBody>
          ) : debtors.data.length === 0 ? (
            <EmptyState title={t('No outstanding balances')} compact />
          ) : (
            <ol className="divide-y divide-line">
              {debtors.data.map((d, i) => (
                <li key={d.studentId} className="flex items-center gap-3 px-5 py-3">
                  <span className="w-5 text-right text-xs text-muted tabular">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/admin/finance/students/${d.studentId}/ledger`}
                      className="block truncate text-sm font-medium text-ink hover:text-brand-700"
                    >
                      {d.name}
                    </Link>
                    <p className="truncate text-xs text-muted">
                      {d.className ?? '—'} ·{' '}
                      {t('{{count}} open · oldest due {{date}}', {
                        count: d.invoiceCount,
                        formatted: formatNumber(d.invoiceCount),
                        date: formatDate(d.oldestDueDate),
                      })}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-ink tabular">{money(d.outstanding)}</span>
                </li>
              ))}
            </ol>
          )}
        </Card>
      </div>
    </>
  )
}
